import React, {useEffect, useContext, useMemo} from 'react';
import { Link } from "@react-navigation/web";
import {FormattedMessage, IntlContext } from 'react-intl';
import { API } from 'aws-amplify';
import Spinner from '../../components/Spinner';
import { currentLang, currentLangParam } from '../../components/components/utils';
import AppContext from '../../components/AppContext';
import {constants} from '../../constants'
import PopupLink from '../../templates/PopupLink';
function Account({navigation}) {
	const appContext = useContext(AppContext);
	const { messages } = useContext(IntlContext);

	const labels = useMemo(() => {
		const formatValues = {
			p: (chunks) => <p>{chunks}</p>,
			a: (chunks) => (
				<a
					href={`https://www.cbsa-asfc.gc.ca/contact/bis-sif-${document.documentElement.lang === "fr" ? "fra" : "eng"}.html`}
					className="App-link"
					target="_blank"
					rel="noreferrer"
				>
					{chunks}
				</a>
			),
		};

		return {
			"account.edit.breadcrumbs": (
				<FormattedMessage
					id="account.edit.breadcrumbs"
				/>
			),
			"http.networkError": (
				<FormattedMessage
					id="http.networkError"
					values={formatValues}
				/>
			),
			"http.networkError.args": (errorNum) => (
				<FormattedMessage
					id="http.networkError.args"
					values={{
						...formatValues,
						num: errorNum ? `01-0001-${errorNum}` : "01-0001-Unknown",

					}}
				/>
			),
		};
	}, []); // Empty dependency array since formatValues is static and doesn't depend on intl

	const [accountData, setAccountData] = React.useState({...appContext.accountData});
	const [contactData, setContactData] = React.useState({...appContext.contactData});
	const [showSpinner, setShowSpinner] = React.useState(isItTimeToRefreshData());	//if it's been 30 seconds since the last data retreival date
	if (!appContext.existingUser) {
		navigation.navigate("Account_Edit");
	}

	useEffect(() => {
		if (!appContext.codeTables || appContext.codeTables.length === 0) {
			navigation.navigate("Callback");
		}
		if (showSpinner) {
			Promise.all([
				getAccountData()
				, getContactData()
			]).catch((err) => {
				if (window.env.environment !== "prod") {
					console.log({err})
				}

				let errorNum = err?.response?.data?.Error?.ErrorNumber; // ErrorNumber specific to the lambda

				if (!!errorNum) {
					appContext.setFlashError({duration:-1, messages:[labels["http.networkError.args"](errorNum)]});
				} else {
					appContext.setFlashError({duration:-1, messages:[labels["http.networkError"]]});
				}

			}).finally(() => {
				setShowSpinner(false);
			});
		}
	}, []);

	async function getAccountData() {
		let apiURL = "account";

		return API.get("backend-api", apiURL).then(function(response){
			let responseAccountData = {...response};

			responseAccountData.lastDataRetrievalDate = Date.now();

			setAccountData(responseAccountData);
			appContext.setAccountData(responseAccountData);
		}).catch((err) => {
			if (window.env.environment === "local") {
				let dummyAccountData = {lastDataRetrievalDate: Date.now(), ...window.env["accountData"]};
				setAccountData(dummyAccountData);	//local env, fill with dummy data
				return;
			}
			// TO-DO: possibly add in logic to specify which part of the lambda triggered the error
			throw err;
		});
	}

	async function getContactData() {
		let apiURL = "contact";
		let apiParamsObj = {
			queryStringParameters:{
				"lang": currentLang()
			}
		}

		return API.get("backend-api", apiURL, apiParamsObj).then(function(response){
			let responseContactData = {...response};

			if (!!responseContactData.name && !responseContactData.contactFirstName && !responseContactData.contactLastName) {
				let splitNameArr = responseContactData.name.split(" ", 2);
				responseContactData.contactFirstName = splitNameArr[0];
				responseContactData.contactLastName = splitNameArr[1];
			}

			responseContactData.lastDataRetrievalDate = Date.now();

			setContactData(responseContactData);
			appContext.setContactData(responseContactData);
		}).catch((err) => {
			if (window.env.environment === "local") {
				let dummyContactData = {lastDataRetrievalDate: Date.now(), ...window.env["contactData"]};
				setContactData(dummyContactData);	//local env, fill with dummy data
				return;
			}
			// TO-DO: possibly add in logic to specify which part of the lambda triggered the error
			throw err;
		});
	}

	function isItTimeToRefreshData() {
		return !contactData
		       || !contactData.lastDataRetrievalDate
		       || (typeof contactData.lastDataRetrievalDate !== "number")
		       || Date.now() > contactData.lastDataRetrievalDate + 30000
		       || !accountData
		       || !accountData.lastDataRetrievalDate
		       || (typeof accountData.lastDataRetrievalDate !== "number")
		       || Date.now() > accountData.lastDataRetrievalDate + 30000;
	}

	function handleClick() {
		navigation.navigate("SecureEFiling", currentLangParam());
	}

	return (
		<>
		  <Spinner show={showSpinner} />
		  {!showSpinner ?
		  	<section className="col-md-12">
		  	  <section className="full-width">
		  	    <div className="panel panel-default small">
		  	      <header className="panel-heading">
		  	        <h2 className="panel-title"><FormattedMessage id="account.title"/>
		  	          <span className="iconLink">
		  	            <Link routeName="Account_Edit" params={currentLangParam()}><button title={labels["account.edit.breadcrumbs"]} className="glyphicon glyphicon-edit brdr-0" tabIndex="-1"></button></Link>
		  	          </span>
		  	        </h2>
		  	      </header>
		  	      <dl className="panel-body dl-horizontal brdr-0">
		  	        <dt className="col-md-6"><FormattedMessage id="sima.account.accountInfo.businessNumber"/></dt>
		  	        <dd className="col-md-6">{accountData["ace_businessnumber"]}</dd>
		  	        <dt className="col-md-6"><FormattedMessage id="sima.account.accountInfo.companyLegalName.label"/></dt>
		  	        <dd className="col-md-6">{!!accountData["Name"] ? accountData["Name"] : accountData["companyLegalName"]}</dd>
		  	        <dt className="col-md-6"><FormattedMessage id="sima.account.accountInfo.companyOperatingName.label"/></dt>
		  	        <dd className="col-md-6">{!!accountData["Name"] ? accountData["Name"] : accountData["companyOperatingName"]}</dd>
		  	        <dt className="col-md-6"><FormattedMessage id="sima.account.accountInfo.companyAddress.label"/></dt>
		  	        <dd className="col-md-6">{!!accountData["ace_street"] ? accountData["ace_street"] : accountData["companyAddress"]}</dd>
		  	        <dt className="col-md-6"><FormattedMessage id="sima.account.accountInfo.companyCity.label"/></dt>
		  	        <dd className="col-md-6">{!!accountData["ace_city"] ? accountData["ace_city"] : accountData["companyCity"]}</dd>
		  	        <dt className="col-md-6"><FormattedMessage id="sima.account.accountInfo.companyCountry.label"/></dt>
					<dd className="col-md-6">{!!accountData["dtoCountryRegion"] ? appContext?.codeTableLookup?.countries?.[accountData["dtoCountryRegion"]] : appContext?.codeTableLookup?.countries?.[accountData["companyCountry"]]}</dd>
		  	        <dt className="col-md-6"><FormattedMessage id="sima.account.accountInfo.companyProvince.label"/></dt>
		  	        <dd className="col-md-6">{!!accountData["dtoProvinceState"] ? accountData["dtoProvinceState"] : accountData["companyProvince"]}</dd>
		  	        <dt className="col-md-6"><FormattedMessage id="sima.account.accountInfo.companyPostalZipCode.label"/></dt>
		  	        <dd className="col-md-6">{!!accountData["ace_postalzipcode"] ? accountData["ace_postalzipcode"] : accountData["companyPostalZipCode"]}</dd>
		  	        <dt className="col-md-6"><FormattedMessage id="common.phoneNumber"/></dt>
		  	        <dd className="col-md-6">{accountData["Telephone1"]}</dd>
		  	        <dt className="col-md-6"><FormattedMessage id="sima.account.accountInfo.registrantInfoCheck.radio" values={{i: (chunks) => <i>{chunks}</i>}}/></dt>
		  	        <dd className="col-md-6">{accountData["ace_accounttype"] === constants.registrantInfoFirm ? <FormattedMessage id="label.yes"/> : <FormattedMessage id="label.no"/>}</dd>
		  	      </dl>
		  	    </div>
		  	  </section>
		  	  <section className="full-width">
		  	    <div className="panel panel-default small">
		  	      <header className="panel-heading">
		  	        <h2 className="panel-title"><FormattedMessage id="contact.title"/>
		  	          <span className="iconLink">
		  	            <Link routeName="Account_Edit" params={currentLangParam()}><button title={labels["account.edit.breadcrumbs"]} className="glyphicon glyphicon-edit brdr-0" tabIndex="-1"></button></Link>
		  	          </span>
		  	        </h2>
		  	      </header>
		  	      <dl className="panel-body dl-horizontal brdr-0">
		  	        <dt className="col-md-6" title={messages["sima.account.accountInfo.URN.title"]}><FormattedMessage id="sima.account.accountInfo.URN.label"/></dt>
		  	        <dd className="col-md-6">{contactData["ace_urn"]}</dd>
		  	        <dt className="col-md-6"><FormattedMessage id="sima.account.accountInfo.firstName"/></dt>
		  	        <dd className="col-md-6">{contactData["firstname"]}</dd>
		  	        <dt className="col-md-6"><FormattedMessage id="sima.account.accountInfo.lastName"/></dt>
		  	        <dd className="col-md-6">{contactData["lastname"]}</dd>
		  	        <dt className="col-md-6"><FormattedMessage id="account.emailAddress"/></dt>
		  	        <dd className="col-md-6">{contactData["emailaddress1"]}</dd>
		  	        <dt className="col-md-6"><FormattedMessage id="common.address"/></dt>
		  	        <dd className="col-md-6">{contactData["ace_address"]}</dd>
		  	        <dt className="col-md-6"><FormattedMessage id="common.city"/></dt>
		  	        <dd className="col-md-6">{contactData["ace_city"]}</dd>
		  	        <dt className="col-md-6"><FormattedMessage id="common.country"/></dt>
		  	        <dd className="col-md-6">{appContext.codeTableLookup.countries[contactData["ace_countryregion"]]}</dd>
		  	        <dt className="col-md-6"><FormattedMessage id="common.provinceState"/></dt>
		  	        <dd className="col-md-6">{(!!contactData["ace_provincestate"] && contactData["ace_provincestate"].match(/[0-9a-zA-Z-]{36}/)) ? appContext.codeTableLookup.allProvincesAndStates[contactData["ace_provincestate"]] : (!!contactData["ace_provincestatetext"] ? contactData["ace_provincestatetext"] : contactData["ace_provincestate"])}</dd>
		  	        <dt className="col-md-6"><FormattedMessage id="common.postalCodeZipCode"/></dt>
		  	        <dd className="col-md-6">{contactData["ace_zippostalcode"]}</dd>
		  	      </dl>
		  	    </div>
		  	  </section>
		  	  <div className="form-group col-md-12 text-left">
		  	      <button className="link" onClick={handleClick}><FormattedMessage id="common.fill-a-E-Filing-form" /></button>
		  	  </div>
		  	</section>
		  : <></>
		  }
		</>
	);
}

Account.path = "";
Account.navigationOptions = {
	pageTitle: <FormattedMessage id="account.title" />
	, breadcrumb: [
		<PopupLink url={window.env["canada-url"][currentLang()]}><FormattedMessage id="breadcrumb.Canada.text" /></PopupLink>
		, <PopupLink url={window.env["cbsa-breadcrumbs-url"][currentLang()]}><FormattedMessage id="breadcrumb.CBSA.text" /></PopupLink>
		, <PopupLink url={window.env["cbsa-sima-breadcrumbs-url"][currentLang()]}><FormattedMessage id="breadcrumb.ACE.text" /></PopupLink>
	]
	, breadcrumbTitle: <FormattedMessage id="account.title"/>
};

export default Account;